/* eslint-disable prettier/prettier */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable object-shorthand */
import React, { useState, useRef, useCallback, useEffect } from 'react'
import _ from 'lodash'
import * as VideoExpress from '@vonage/video-express'
import cameraSubscriber from '@vonage/video-express/dist/mp/camera-subscriber'
import ScreenSubscriber from '@vonage/video-express/dist/mp/screen-subscriber'

interface Participant {
  id: string;
  name: string;
  camera?: cameraSubscriber;
  screen?: ScreenSubscriber;
  connectionData?: string;
  isMe?: boolean;
}

interface VideoFilter {
  filterName: string;
  filterPayload: any;
}

interface RoomRef {
  current: VideoExpress.Room | null;
}

interface PublisherOptions {
  style: {
    buttonDisplayMode: 'off' | 'auto';
    nameDisplayMode: 'auto' | 'off';
    audioLevelDisplayMode: 'off' | 'auto';
  };
  name: string;
  showControls: boolean;
}

export default function useRoom() {
  const roomRef: RoomRef = useRef(null)
  const publisherOptionsRef = useRef<PublisherOptions | null>(null)

  const [camera, setCamera] = useState<any | null>(null)
  const [screen, setScreen] = useState<any| null>(null)
  const [localParticipant, setLocalParticipant] = useState<Participant | null>(null)
  const [connected, setConnected] = useState<boolean>(false)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [networkStatus, setNetworkStatus] = useState<'disconnected' | 'reconnected' | 'reconnecting' | null>(null)
  const [publisherIsSpeaking, setPublisherIsSpeaking] = useState<boolean>(false)
  const [cameraPublishing, setCameraPublishing] = useState<boolean>(false)

  const addParticipants = (participant: Participant) => {
    setParticipants(prev => [...prev, participant])
  }

  const removeParticipants = ({ participant }: { participant: Participant }) => {
    setParticipants(prev =>
      prev.filter(prevparticipant => prevparticipant.id !== participant.id)
    )
  }

  const addLocalParticipant = ({ room }: { room: VideoExpress.Room }) => {
    if (room) {
      setLocalParticipant({
        id: room.participantId,
        name: room.participantName
      })
    }
  }

  const removeLocalParticipant = ({ participant }: { participant: Participant }) => {
    setParticipants(null)
  }

  const onAudioLevel = useCallback((audioLevel: number) => {
    let movingAvg: number | null = null
    if (movingAvg === null || movingAvg <= audioLevel) {
      movingAvg = audioLevel
    } else {
      movingAvg = 0.8 * movingAvg + 0.2 * audioLevel
    }
    // 1.5 scaling to map the -30 - 0 dBm range to [0,1]
    const currentLogLevel = Math.log(movingAvg) / Math.LN10 / 1.5 + 1
    if (currentLogLevel > 0.4) {
      setPublisherIsSpeaking(true)
    } else {
      setPublisherIsSpeaking(false)
    }
  }, [])

  const addPublisherCameraEvents = (publisher: any) => {
    publisher.on(
      'audioLevelUpdated',
      _.throttle((event: any) => onAudioLevel(event), 250)
    )
  }

  const parseVideoFilter = (videoFilter: VideoFilter) => {
    switch (videoFilter.filterName) {
      case 'blur':
        return {
          type: 'backgroundBlur',
          blurStrength: videoFilter.filterPayload
        }
      case 'backgroundImage':
        return {
          type: 'backgroundReplacement',
          backgroundImgUrl: videoFilter.filterPayload
        }
      default:
        return {}
    }
  }

  const startRoomListeners = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.on('connected', () => {
        console.log('Room: connected')
      })
      roomRef.current.on('disconnected', () => {
        setNetworkStatus('disconnected')
        console.log('Room: disconnected')
      })
      roomRef.current.camera.on('created', () => {
        setCameraPublishing(true)
        console.log('camera publishing now')
      })
      roomRef.current.on('activeSpeakerChanged', (participant: Participant) => {
        console.log('Active speaker changed', participant)
      })

      roomRef.current.on('reconnected', () => {
        setNetworkStatus('reconnected')
        console.log('Room: reconnected')
      })
      roomRef.current.on('reconnecting', () => {
        setNetworkStatus('reconnecting')
        console.log('Room: reconnecting')
      })
      roomRef.current.on('participantJoined', (participant: Participant) => { 
         addParticipants(participant)        
        console.log('Participant joined: ', participant)
      })

      roomRef.current.on('participantLeft', (participant: Participant, reason: string) => {
        removeParticipants({ participant })
        console.log('Room: participant left', participant, reason)
      })
    }
  }, [])

  const createCall = useCallback(
    async (
      { apikey, sessionId, token }: { apikey: string; sessionId: string; token: string },
      userName, 
      videoFilter, 
      publisherOptions
    ) => {
      if (!apikey || !sessionId || !token) {
        throw new Error('Check your credentials')
      }

      roomRef.current = new VideoExpress.Room({
        apiKey: apikey,
        sessionId: sessionId,
        token: token,
        roomContainer: 'roomContainer',
        maxVideoParticipantsOnScreen: 25,
        participantName: userName,
        managedLayoutOptions: {
          layoutMode: 'grid',
          screenPublisherContainer: 'screenSharingContainer',
          speakerHighlightEnabled: true
        }
      })
      startRoomListeners()

      if (videoFilter && videoFilter.filterName && videoFilter.filterPayload) {
        publisherOptionsRef.current = { ...publisherOptions, style: {
            buttonDisplayMode: 'off',
            nameDisplayMode: 'auto',
            audioLevelDisplayMode: 'off'
          },
          name: userName,
          showControls: true,
          videoFilter: parseVideoFilter(videoFilter)}
      } else {
        publisherOptionsRef.current = { ...publisherOptions, style: {
            buttonDisplayMode: 'off',
            nameDisplayMode: 'auto',
            audioLevelDisplayMode: 'off'
          },
          name: userName,
          showControls: true}
      }

      
      roomRef.current
      .join({ publisherProperties: publisherOptionsRef.current })
      .then(() => {
        setConnected(true)
        setCamera(roomRef.current.camera)
        setScreen(roomRef.current.screen)
        addLocalParticipant({ room: roomRef.current })
        addPublisherCameraEvents(roomRef.current.camera)
      })
      .catch((e: Error) => console.log(e))
      console.log('[useRoom] - finalPublisherOptions', publisherOptionsRef.current)
    },
    []
  )

const join = useCallback(
    async (  { apikey, sessionId, token }: { apikey: string; sessionId: string; token: string },
      userName, 
      videoFilter, 
      publisherOptions,
      room) => {

      roomRef.current = room
      await roomRef.current.leave()

      if(roomRef.current != null){
        roomRef.current = null
      }   
      
      console.log('room join',roomRef.current)

      roomRef.current = new VideoExpress.Room({
        apiKey: apikey,
        sessionId,
        token,
        roomContainer: 'roomContainer',
        maxVideoParticipantsOnScreen: 25,
        participantName: userName,
        managedLayoutOptions: {
          layoutMode: 'grid',
          screenPublisherContainer: 'screenSharingContainer',
          speakerHighlightEnabled: true
        }
      })

      if (videoFilter && videoFilter.filterName && videoFilter.filterPayload) {
        publisherOptionsRef.current = { ...publisherOptions, style: {
            buttonDisplayMode: 'off',
            nameDisplayMode: 'auto',
            audioLevelDisplayMode: 'off'
          },
          name: userName,
          showControls: true,
          videoFilter: parseVideoFilter(videoFilter)}
      } else {
        publisherOptionsRef.current = { ...publisherOptions, style: {
            buttonDisplayMode: 'off',
            nameDisplayMode: 'auto',
            audioLevelDisplayMode: 'off'
          },
          name: userName,
          showControls: true}
      }

      roomRef.current
      .join({ publisherProperties: publisherOptionsRef.current })
      .then(() => {
        setConnected(true)
        setCamera(roomRef.current.camera)
        setScreen(roomRef.current.screen)
        addLocalParticipant({ room: roomRef.current })
        addPublisherCameraEvents(roomRef.current.camera)
      })
      .catch((e: Error) => console.log(e))
      console.log('[VideoCallContext JOiN] - finalPublisherOptions', publisherOptionsRef.current)
  },[]
  )

  const leaveRoom = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.leave()
      if(roomRef.current != null){
        roomRef.current = null
      }
      setConnected(false)
      setCamera(null)
      setScreen(null)
      setParticipants([])
      setLocalParticipant(null)
    }
  }, [])

  return {
    createCall,
    connected,
    camera,
    screen,
    room: roomRef.current,
    participants,
    networkStatus,
    publisherIsSpeaking,
    cameraPublishing,
    localParticipant,
    leaveRoom,
    join
  }
}
